// @ts-nocheck
import { useState } from 'react';
import {
  LayoutDashboard,
  Puzzle,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Calendar,
  ListTodo,
  HelpCircle,
  Megaphone,
  Building2,
  MessageSquarePlus,
  Gauge,
  Download,
  ArrowLeftRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { hasPermission, type UserRole, edgeFnUrl } from '@saas-boilerplate/shared';
import appLogo from '@/assets/upstand_ai_logo.svg';
import { cannyConfigured } from '@/lib/canny';
import { track } from '@/lib/analytics';

/**
 * Which desktop build to offer this visitor, or null on a platform we don't ship. iPadOS
 * masquerades as "MacIntel" but reports multi-touch, so exclude touch devices - the app is a
 * desktop (Electron) build. Windows on ARM runs the x64 build under emulation, so x64 is the
 * right offer there too.
 *
 * The button only appears if the feed actually has a release for the key (see downloadDesktopApp),
 * so a platform we haven't published yet shows nothing rather than a dead button.
 */
const DESKTOP_TARGET: { platform: string; label: string } | null = (() => {
  if (typeof navigator === 'undefined') return null;
  const plat = navigator.platform || '';
  const touch = (navigator.maxTouchPoints ?? 0) > 1;
  if (/Mac/i.test(plat) && !touch) return { platform: 'darwin-arm64', label: 'Mac' };
  if (/Win/i.test(plat)) return { platform: 'win32-x64', label: 'Windows' };
  return null;
})();

interface SidebarProps {
  activeView: string;
  onNavigate: (view: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  userProfile?: any;
  /** Internal Upstand team member - gates the staff-only Leaderboard nav item. */
  isStaff?: boolean;
  onSignOut?: () => void;
  onSwitchWorkspace?: () => void;
  workspaceName?: string;
  onHelp?: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: any;
  requiredPermission?: string;
  /** If set, clicking navigates to this view id instead of `id` */
  navigateAs?: string;
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, requiredPermission: 'canAccessDashboard' },
  { id: 'meetings', label: 'Meetings', icon: Calendar, requiredPermission: 'canAccessMeetings' },
  {
    id: 'action_items',
    label: 'Action items',
    icon: ListTodo,
    requiredPermission: 'canAccessMeetings',
  },
];

export function Sidebar({
  activeView,
  onNavigate,
  isOpen,
  onToggle,
  userProfile,
  isStaff,
  onSignOut,
  onSwitchWorkspace,
  workspaceName,
  onHelp,
}: SidebarProps) {
  const [downloadingApp, setDownloadingApp] = useState(false);

  // Fetch a FRESH signed download URL at click time (the desktop-update feed signs for 1h), then
  // hand the browser the storage URL - the installer downloads directly, no page navigation.
  // format=installer is the human-install artifact for the platform (DMG on macOS, Setup.exe on
  // Windows), as opposed to the default zip/nupkg the in-app updater consumes.
  const downloadDesktopApp = async () => {
    if (downloadingApp || !DESKTOP_TARGET) return;
    const { platform, label } = DESKTOP_TARGET;
    setDownloadingApp(true);
    try {
      const res = await fetch(
        `${edgeFnUrl('desktop-update')}?version=0.0.0&platform=${platform}&format=installer`,
      );
      if (res.status === 204 || res.status === 404) {
        toast.info(`No ${label} build is published yet - check back soon.`);
        return;
      }
      if (!res.ok) throw new Error(`feed ${res.status}`);
      const json = await res.json();
      if (!json?.url) throw new Error('no url in feed');
      track('desktop_app_downloaded', { platform });
      window.location.assign(json.url);
    } catch {
      toast.error(`Could not fetch the ${label} app download. Please try again.`);
    } finally {
      setDownloadingApp(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'member':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'member':
        return 'Member';
      default:
        return 'Member';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const userRole = userProfile?.role as UserRole;

  return (
    <div
      className={`fixed left-0 top-0 h-full bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-300 z-50 shadow-md ${isOpen ? 'w-64' : 'w-16'}`}
    >
      <div className="flex flex-col h-full">
        <div className="relative min-h-[52px] px-3 py-2.5">
          {isOpen && (
            <div className="pr-10">
              {/* SVG viewBox is cropped to text (see upstand_ai_logo.svg); ~192px wide → ~51px tall */}
              <div className="aspect-[370/98] w-full max-w-full min-h-0">
                <img
                  src={appLogo}
                  alt="Upstand.ai"
                  width={370}
                  height={98}
                  decoding="async"
                  className="block h-full w-full object-contain object-left"
                />
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className={`absolute h-8 w-8 shrink-0 p-0 hover:bg-sidebar-accent ${
              isOpen ? 'right-2 top-1/2 -translate-y-1/2' : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
            }`}
          >
            {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        <nav className="flex-1 p-3">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const target = item.navigateAs ?? item.id;
              const isActiveRow = activeView === item.id;
              const hasAccess =
                !item.requiredPermission || hasPermission(userRole, item.requiredPermission as any);

              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => hasAccess && onNavigate(target)}
                    disabled={!hasAccess}
                    className={`w-full flex items-center rounded-full transition-colors duration-200 ${
                      !hasAccess
                        ? 'text-muted-foreground cursor-not-allowed opacity-50'
                        : isActiveRow
                          ? 'bg-white text-primary shadow-sm border border-border/60'
                          : 'text-sidebar-foreground/85 hover:bg-sidebar-accent/90 hover:text-foreground'
                    } ${isOpen ? 'px-3 py-2' : 'px-2 py-2 justify-center rounded-lg'}`}
                  >
                    <Icon className={`h-4 w-4 flex-shrink-0 ${isActiveRow && hasAccess ? 'text-primary' : ''}`} />
                    {isOpen && <span className="ml-3 text-sm font-medium truncate">{item.label}</span>}
                  </button>
                </li>
              );
            })}

            {/* Staff-only: admin dashboard (usage analytics + referral leaderboard; also served at
                admin.upstand.ai). Hidden for non-staff; the pages + their RPCs also enforce is_staff
                server-side (migrations 0103/0108), so hiding here is UX, not security. */}
            {isStaff && (
              <li key="admin">
                <button
                  type="button"
                  onClick={() => onNavigate('admin')}
                  className={`w-full flex items-center rounded-full transition-colors duration-200 ${
                    activeView === 'admin' || activeView === 'leaderboard'
                      ? 'bg-white text-primary shadow-sm border border-border/60'
                      : 'text-sidebar-foreground/85 hover:bg-sidebar-accent/90 hover:text-foreground'
                  } ${isOpen ? 'px-3 py-2' : 'px-2 py-2 justify-center rounded-lg'}`}
                  title={isOpen ? undefined : 'Admin'}
                >
                  <Gauge className={`h-4 w-4 flex-shrink-0 ${activeView === 'admin' || activeView === 'leaderboard' ? 'text-primary' : ''}`} />
                  {isOpen && <span className="ml-3 text-sm font-medium truncate">Admin</span>}
                </button>
              </li>
            )}
          </ul>
        </nav>

        {/* Account block - identity, settings, help, what's new, sign out (design review). */}
        <div className="shrink-0 border-t border-sidebar-border p-3">
          {isOpen ? (
            <div className="space-y-3">
              <div className="px-2">
                <p className="truncate text-sm font-medium text-muted-foreground" title={userProfile?.email || undefined}>
                  {userProfile?.email || 'You'}
                </p>
                <p className="truncate text-sm text-muted-foreground" title={workspaceName || undefined}>
                  {workspaceName || 'Your workspace'}
                </p>
              </div>

              <div className="space-y-0.5 border-t border-sidebar-border pt-3">
                {DESKTOP_TARGET && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={downloadingApp}
                    onClick={() => void downloadDesktopApp()}
                    className="mb-2 w-full justify-center gap-2 border-border/70 bg-white/60"
                  >
                    <Download className="h-4 w-4 shrink-0" />
                    {downloadingApp ? 'Preparing download...' : `Download ${DESKTOP_TARGET.label} app`}
                  </Button>
                )}
                {hasPermission(userRole, 'canAccessPersonalSettings') && (
                <button
                  type="button"
                  onClick={() => onNavigate('settings')}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors ${
                    activeView === 'settings'
                      ? 'bg-sidebar-accent/80 text-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent/80 hover:text-foreground'
                  }`}
                >
                  <Settings className="h-4 w-4 shrink-0" />
                  Settings
                </button>
                )}
                {hasPermission(userRole, 'canAccessGeneralSettings') && (
                <button
                  type="button"
                  onClick={() => onNavigate('org_settings')}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors ${
                    activeView === 'org_settings'
                      ? 'bg-sidebar-accent/80 text-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent/80 hover:text-foreground'
                  }`}
                >
                  <Building2 className="h-4 w-4 shrink-0" />
                  Organisation Settings
                </button>
                )}
                {hasPermission(userRole, 'canAccessIntegrations') && (
                <button
                  type="button"
                  onClick={() => onNavigate('integrations')}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors ${
                    activeView === 'integrations'
                      ? 'bg-sidebar-accent/80 text-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent/80 hover:text-foreground'
                  }`}
                >
                  <Puzzle className="h-4 w-4 shrink-0" />
                  Integrations
                </button>
                )}
                <a
                  href="https://upstand.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent/80 hover:text-foreground"
                >
                  <HelpCircle className="h-4 w-4 shrink-0" />
                  Help
                </a>
                {cannyConfigured() && (
                <button
                  type="button"
                  onClick={() => onNavigate('feedback')}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors ${
                    activeView === 'feedback'
                      ? 'bg-sidebar-accent/80 text-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent/80 hover:text-foreground'
                  }`}
                >
                  <MessageSquarePlus className="h-4 w-4 shrink-0" />
                  Feedback
                </button>
                )}
                <button
                  type="button"
                  onClick={() => onNavigate('releases')}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors ${
                    activeView === 'releases'
                      ? 'bg-sidebar-accent/80 text-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent/80 hover:text-foreground'
                  }`}
                >
                  <Megaphone className="h-4 w-4 shrink-0" />
                  What's new
                </button>
              </div>

              {(onSwitchWorkspace || onSignOut) && (
                <div className="border-t border-sidebar-border pt-3">
                  {onSwitchWorkspace && (
                    <button
                      type="button"
                      onClick={onSwitchWorkspace}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent/80 hover:text-foreground"
                    >
                      <ArrowLeftRight className="h-4 w-4 shrink-0" />
                      Switch workspace
                    </button>
                  )}
                  {onSignOut && (
                    <button
                      type="button"
                      onClick={onSignOut}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent/80 hover:text-foreground"
                    >
                      <LogOut className="h-4 w-4 shrink-0" />
                      Sign out
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              {DESKTOP_TARGET && (
              <button
                type="button"
                onClick={() => void downloadDesktopApp()}
                disabled={downloadingApp}
                title={`Download ${DESKTOP_TARGET.label} app`}
                className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
              </button>
              )}
              {hasPermission(userRole, 'canAccessPersonalSettings') && (
              <button
                type="button"
                onClick={() => onNavigate('settings')}
                title="Settings"
                className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground"
              >
                <Settings className="h-4 w-4" />
              </button>
              )}
              {hasPermission(userRole, 'canAccessGeneralSettings') && (
              <button
                type="button"
                onClick={() => onNavigate('org_settings')}
                title="Organisation Settings"
                className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground"
              >
                <Building2 className="h-4 w-4" />
              </button>
              )}
              {hasPermission(userRole, 'canAccessIntegrations') && (
              <button
                type="button"
                onClick={() => onNavigate('integrations')}
                title="Integrations"
                className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground"
              >
                <Puzzle className="h-4 w-4" />
              </button>
              )}
              <a
                href="https://upstand.ai"
                target="_blank"
                rel="noopener noreferrer"
                title="Help"
                className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground"
              >
                <HelpCircle className="h-4 w-4" />
              </a>
              {cannyConfigured() && (
              <button
                type="button"
                onClick={() => onNavigate('feedback')}
                title="Feedback"
                className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground"
              >
                <MessageSquarePlus className="h-4 w-4" />
              </button>
              )}
              <button
                type="button"
                onClick={() => onNavigate('releases')}
                title="What's new"
                className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground"
              >
                <Megaphone className="h-4 w-4" />
              </button>
              {onSwitchWorkspace && (
                <button
                  type="button"
                  onClick={onSwitchWorkspace}
                  title="Switch workspace"
                  className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground"
                >
                  <ArrowLeftRight className="h-4 w-4" />
                </button>
              )}
              {onSignOut && (
                <button
                  type="button"
                  onClick={onSignOut}
                  title="Sign out"
                  className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/90 hover:text-foreground"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
